<template>
    <article class="allocation-pool-card">
        <header>
            <button type="button" class="allocation-pool-toggle" :aria-expanded="String(expanded)" :aria-controls="detailsId" @click="expanded = !expanded">
                <span class="allocation-pool-identity">
                    <strong>{{ pool.label }}</strong>
                    <small>{{ pool.clusterName || 'Platform cluster' }}</small>
                </span>
                <span class="allocation-pool-summary">
                    <span>{{ pool.nodes.length }} recorded workers · {{ pool.spaces.length }} classes</span>
                    <small>{{ reservationSummary }}</small>
                </span>
                <v-icon>{{ expanded ? 'mdi-chevron-up' : 'mdi-chevron-down' }}</v-icon>
            </button>
        </header>
        <div v-if="expanded" :id="detailsId">
            <div class="allocation-pool-context">
                <span>{{ selectedSpace ? selectedSpace.label : 'All classes' }}</span>
                <v-btn v-if="selectedSpace" small text color="primary" @click="$emit('select-space', null)">Clear class</v-btn>
                <v-spacer />
                <v-tooltip bottom>
                    <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" tabindex="0">
                            Loaded-worker capacity
                            <v-icon small>mdi-information-outline</v-icon>
                        </span>
                    </template>
                    <span>Capacity covers the returned worker lifetimes, not an inventory of every worker in the pool.</span>
                </v-tooltip>
                <v-tooltip v-if="pool.unplacedCount" bottom>
                    <template #activator="{ on, attrs }">
                        <span v-bind="attrs" v-on="on" tabindex="0">
                            {{ pool.unplacedCount }} unresolved placements
                            <v-icon small>mdi-information-outline</v-icon>
                        </span>
                    </template>
                    <span>Pool-attributed allocation can be known even when worker placement cannot be uniquely resolved.</span>
                </v-tooltip>
            </div>
            <NodeAllocationTimeline
                :resources="pool.resources"
                :start="start"
                :end="end"
                :selection="hoveredContributionSelection || classSelection"
                :contributions="contributions"
                :hovered-contribution-key="hoveredContributionKey"
                :clear-selection-enabled="!hoveredContributionKey"
                :pinned-at="pinnedAt"
                :zoom-can-undo="zoomCanUndo"
                :zoom-can-reset="zoomCanReset"
                @clear-selection="$emit('select-space', null)"
                @hover-contribution="hoveredContributionKey = $event"
                @inspect="inspectedAt = $event"
                @pin="$emit('pin', $event)"
                @zoom="$emit('zoom', $event)"
                @undo-zoom="$emit('undo-zoom')"
                @reset-zoom="$emit('reset-zoom')" />
            <div class="allocation-pool-details">
                <div class="allocation-pool-section-heading">
                    <strong>Classes</strong>
                    <small>{{ inspectionTime === null ? 'Recorded peaks · reserved / limit' : timestamp(inspectionTime) + ' · reserved / limit' }}</small>
                </div>
                <div class="allocation-pool-table-scroll">
                    <table class="allocation-pool-spaces">
                        <thead>
                            <tr>
                                <th>Space / class</th>
                                <th v-for="metric in pool.resources" :key="metric.key">
                                    {{ metric.label }}
                                    <small>{{ metric.unit }}</small>
                                </th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr
                                v-for="space in sortedSpaces"
                                :key="space.key"
                                :class="{ 'allocation-pool-space-selected': selectedSpace && selectedSpace.key === space.key }">
                                <td>
                                    <v-btn
                                        small
                                        text
                                        color="primary"
                                        :aria-pressed="String(Boolean(selectedSpace && selectedSpace.key === space.key))"
                                        @click="toggleSpace(space)">
                                        {{ space.label }}
                                    </v-btn>
                                </td>
                                <td v-for="metric in pool.resources" :key="metric.key">
                                    <span>{{ spaceValue(space, metric.key, 'requests') }} / {{ spaceValue(space, metric.key, 'limits') }}</span>
                                    <v-tooltip v-if="spaceWarning(space, metric.key)" bottom>
                                        <template #activator="{ on, attrs }">
                                            <span v-bind="attrs" v-on="on" tabindex="0" aria-label="Incomplete allocation records">
                                                <v-icon small color="warning">mdi-alert-circle-outline</v-icon>
                                            </span>
                                        </template>
                                        <span>{{ spaceWarning(space, metric.key) }}</span>
                                    </v-tooltip>
                                </td>
                            </tr>
                        </tbody>
                    </table>
                </div>
                <p v-if="selectedSpace && !activeSpace" class="allocation-pool-empty">No recorded allocation for this class in the displayed window.</p>
                <div v-if="pool.nodes.length" class="allocation-pool-section-heading">
                    <strong>Workers</strong>
                    <small>Hover or focus a worker or its graph band to compare · select to open its node card</small>
                </div>
                <div v-if="pool.nodes.length" class="allocation-pool-workers">
                    <button
                        v-for="entry in sortedNodes"
                        :key="workerKey(entry)"
                        type="button"
                        :class="['allocation-pool-worker', { 'allocation-pool-worker--highlighted': hoveredContributionKey === workerKey(entry) }]"
                        @mouseenter="hoveredContributionKey = workerKey(entry)"
                        @mouseleave="leaveContribution(workerKey(entry), $event)"
                        @focus="hoveredContributionKey = workerKey(entry)"
                        @blur="hoveredContributionKey = null"
                        @click="$emit('inspect-node', entry)">
                        <span>
                            <v-icon small>mdi-server-outline</v-icon>
                            {{ entry.node.node_name }}
                        </span>
                        <small>{{ timestamp(entry.node.start_time) }} → {{ entry.node.stop_time ? timestamp(entry.node.stop_time) : 'Open' }}</small>
                        <v-icon small>mdi-arrow-right</v-icon>
                    </button>
                </div>
                <section v-if="unresolvedSeries.length" class="allocation-pool-unresolved-section">
                    <div class="allocation-pool-section-heading">
                        <strong>Placement unresolved</strong>
                        <small>{{ unresolvedSeries.length }} recorded {{ unresolvedSeries.length === 1 ? 'session' : 'sessions' }}</small>
                    </div>
                    <p class="allocation-pool-empty">
                        These sessions are attributed to this nodepool, but not to a particular worker. Recorded reservations and limits do not establish
                        whether resources were fulfilled.
                    </p>
                    <div class="allocation-pool-unresolved-rows">
                        <div
                            v-for="item in unresolvedSeries"
                            :key="item.key"
                            tabindex="0"
                            role="group"
                            :class="['allocation-pool-unresolved-row', { 'allocation-pool-unresolved-row--highlighted': hoveredContributionKey === item.key }]"
                            @mouseenter="hoveredContributionKey = item.key"
                            @mouseleave="leaveContribution(item.key, $event)"
                            @focus="hoveredContributionKey = item.key"
                            @blur="hoveredContributionKey = null">
                            <strong>{{ item.session.image_name || 'Application ' + item.session.aid }}</strong>
                            <small>
                                {{ timestamp(item.session.start_time) }} → {{ item.session.stop_time ? timestamp(item.session.stop_time) : 'Running' }}
                            </small>
                            <small>Recorded worker: {{ item.session.worker_node_name || 'Not recorded' }} · placement unresolved</small>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    </article>
</template>

<script>
import NodeAllocationTimeline from './NodeAllocationTimeline.vue'
import { formatAllocationTime, formatResourceMeasurement, resourceMeasurementWarning, recordedTime, sessionAllocationSummary } from './nodeAllocationData'

export default {
    name: 'NodeAllocationPoolCard',
    components: { NodeAllocationTimeline },
    props: {
        pool: { type: Object, required: true },
        start: { type: Number, required: true },
        end: { type: Number, required: true },
        pinnedAt: { type: Number, default: null },
        zoomCanUndo: { type: Boolean, default: false },
        zoomCanReset: { type: Boolean, default: false },
        selectedSpace: { type: Object, default: null },
        initiallyExpanded: { type: Boolean, default: false }
    },
    data() {
        return { expanded: this.initiallyExpanded, inspectedAt: null, hoveredContributionKey: null }
    },
    computed: {
        detailsId() {
            return 'allocation-pool-' + encodeURIComponent(this.pool.key)
        },
        inspectionTime() {
            return this.pinnedAt === null ? this.inspectedAt : this.pinnedAt
        },
        reservationSummary() {
            return this.pool.resources
                .filter(metric => metric.key === 'cpu' || metric.key === 'memory')
                .map(metric => {
                    return metric.label + ' ' + formatResourceMeasurement({ value: metric.requested, status: metric.requestStatus }, metric.unit)
                })
                .join(' · ')
        },
        activeSpace() {
            return this.selectedSpace ? this.pool.spaces.find(space => space.key === this.selectedSpace.key) || null : null
        },
        classSelection() {
            if (!this.selectedSpace) return null
            return { label: this.selectedSpace.label, start: this.start, end: this.end, resources: this.activeSpace ? this.activeSpace.resources : [] }
        },
        sortedNodes() {
            return this.pool.nodes.length < 2
                ? this.pool.nodes
                : [...this.pool.nodes].sort(
                      (left, right) =>
                          recordedTime(left.node.start_time) - recordedTime(right.node.start_time) || this.workerKey(left).localeCompare(this.workerKey(right))
                  )
        },
        workerSeries() {
            const poolMetrics = new Map()
            for (const metric of this.pool.resources) poolMetrics.set(metric.key, metric)
            return this.sortedNodes.map(entry => {
                const points = entry.resources[0]?.points || []
                const resources = []
                for (const resource of entry.resources) {
                    const poolMetric = poolMetrics.get(resource.key)
                    if (!poolMetric) continue
                    if (poolMetric.unit === resource.unit) {
                        resources.push(resource)
                        continue
                    }
                    const rawBudget =
                        poolMetric.unit === 'GPU budget units' &&
                        resource.unit === '%' &&
                        (resource.key === 'nvidia.com/gpucores' || resource.key === 'nvidia.com/gpumem-percentage')
                    if (!rawBudget || !(resource.multiplier > 0)) continue
                    const factor = 1 / resource.multiplier
                    const scale = value => (value === null || value === undefined ? value : value * factor)
                    resources.push({
                        ...resource,
                        unit: poolMetric.unit,
                        requested: scale(resource.requested),
                        limit: scale(resource.limit),
                        points:
                            factor === 1
                                ? resource.points
                                : resource.points.map(point => ({ ...point, requested: scale(point.requested), limit: scale(point.limit) }))
                    })
                }
                return {
                    key: this.workerKey(entry),
                    kind: 'worker',
                    label: `Worker ${entry.node.node_name}`,
                    start: points[0]?.at ?? this.start,
                    end: points[points.length - 1]?.at ?? this.end,
                    resources
                }
            })
        },
        unresolvedSeries() {
            const entries = []
            const sessions = this.pool.unplaced || []
            for (let index = 0; index < sessions.length; index++) {
                const session = sessions[index]
                const start = Math.max(this.start, recordedTime(session.start_time))
                const end = Math.min(this.end, session.stop_time ? recordedTime(session.stop_time) : this.end)
                if (!Number.isFinite(start) || !Number.isFinite(end) || start >= end) continue
                const resources = this.pool.resources.map(resource => {
                    const summary = sessionAllocationSummary(session, resource)
                    const values = {
                        requested: summary.requests.value,
                        limit: summary.limits.value,
                        requestStatus: summary.requests.status,
                        limitStatus: summary.limits.status
                    }
                    return {
                        key: resource.key,
                        unit: resource.unit,
                        ...values,
                        points: [
                            { at: start, ...values },
                            { at: end, ...values }
                        ]
                    }
                })
                entries.push({
                    key: `unresolved:${session.session_id ?? index}`,
                    kind: 'unresolved',
                    label: `Placement unresolved · ${session.image_name || 'Application ' + session.aid}`,
                    start,
                    end,
                    resources,
                    session
                })
            }
            return entries.length < 2
                ? entries
                : entries.sort(
                      (left, right) => recordedTime(left.session.start_time) - recordedTime(right.session.start_time) || left.key.localeCompare(right.key)
                  )
        },
        contributions() {
            return [...this.workerSeries, ...this.unresolvedSeries]
        },
        hoveredContributionSelection() {
            return this.contributions.find(item => item.key === this.hoveredContributionKey) || null
        },
        sortedSpaces() {
            const cpuValue = space => space.resources.find(resource => resource.key === 'cpu')?.requested
            return [...this.pool.spaces].sort((left, right) => {
                const difference = (cpuValue(right) ?? -1) - (cpuValue(left) ?? -1)
                return difference || left.label.localeCompare(right.label)
            })
        }
    },
    methods: {
        timestamp: formatAllocationTime,
        measurement(space, key, kind) {
            const metric = space.resources.find(resource => resource.key === key)
            if (!metric) return { value: null, status: 'unrecorded' }
            const valueKey = kind === 'requests' ? 'requested' : 'limit'
            const statusKey = kind === 'requests' ? 'requestStatus' : 'limitStatus'
            if (this.inspectionTime === null) return { value: metric[valueKey], status: metric[statusKey] }
            if (this.inspectionTime < this.start || this.inspectionTime >= this.end) return { value: null, status: 'unrecorded' }
            let point = null
            for (const candidate of metric.points) {
                if (candidate.at > this.inspectionTime) break
                point = candidate
            }
            return point ? { value: point[valueKey], status: point[statusKey] } : { value: null, status: 'unrecorded' }
        },
        spaceValue(space, key, kind) {
            return formatResourceMeasurement(this.measurement(space, key, kind))
        },
        spaceWarning(space, key) {
            return [...new Set(['requests', 'limits'].map(kind => resourceMeasurementWarning(this.measurement(space, key, kind), kind)).filter(Boolean))].join(
                '. '
            )
        },
        toggleSpace(space) {
            this.$emit('select-space', this.selectedSpace && this.selectedSpace.key === space.key ? null : space)
        },
        workerKey(entry) {
            return JSON.stringify([entry.node.tcid ?? null, entry.node.nhid ?? entry.node.node_uid ?? entry.node.node_name, entry.node.start_time])
        },
        leaveContribution(key, event) {
            if (event.currentTarget !== document.activeElement && this.hoveredContributionKey === key) this.hoveredContributionKey = null
        }
    },
    watch: {
        initiallyExpanded(value) {
            if (value) this.expanded = true
        },
        expanded(value) {
            if (!value) this.hoveredContributionKey = null
        },
        start() {
            this.inspectedAt = null
            this.hoveredContributionKey = null
        },
        end() {
            this.inspectedAt = null
            this.hoveredContributionKey = null
        },
        pool() {
            this.hoveredContributionKey = null
        }
    }
}
</script>

<style scoped>
.allocation-pool-card {
    border: 1px solid #e0e7ef;
    border-radius: 8px;
    background: white;
    overflow: hidden;
}
.allocation-pool-toggle {
    display: flex;
    width: 100%;
    align-items: center;
    gap: 16px;
    padding: 16px 20px;
    text-align: left;
    color: inherit;
}
.allocation-pool-toggle:hover,
.allocation-pool-worker:hover {
    background: #f5f9ff;
}
.allocation-pool-identity {
    flex: 1;
    min-width: 0;
}
.allocation-pool-identity strong,
.allocation-pool-identity small,
.allocation-pool-summary small {
    display: block;
}
.allocation-pool-summary {
    font-size: 12px;
    text-align: right;
}
.allocation-pool-card small {
    color: #64748b;
    font-size: 11px;
}
.allocation-pool-context {
    display: flex;
    align-items: center;
    gap: 12px;
    padding: 8px 20px;
    font-size: 11px;
    color: #64748b;
    flex-wrap: wrap;
}
.allocation-pool-details {
    padding: 16px 20px;
}
.allocation-pool-section-heading {
    display: flex;
    gap: 12px;
    align-items: baseline;
    margin: 8px 0 12px;
}
.allocation-pool-table-scroll {
    overflow-x: auto;
    margin-bottom: 16px;
}
.allocation-pool-spaces {
    width: 100%;
    border-collapse: collapse;
    font-size: 12px;
}
.allocation-pool-spaces th,
.allocation-pool-spaces td {
    padding: 8px;
    text-align: left;
    border-bottom: 1px solid #e0e7ef;
    white-space: nowrap;
}
.allocation-pool-spaces th small {
    display: block;
}
.allocation-pool-space-selected {
    background: #f5f9ff;
}
.allocation-pool-workers {
    display: grid;
    gap: 6px;
}
.allocation-pool-worker {
    display: flex;
    align-items: center;
    gap: 12px;
    border: 1px solid #e0e7ef;
    border-radius: 6px;
    padding: 10px;
    text-align: left;
}
.allocation-pool-worker--highlighted,
.allocation-pool-worker--highlighted:hover {
    border-color: #a6326d;
    background: #fbf1f6;
    box-shadow: inset 3px 0 #a6326d;
}
.allocation-pool-worker span {
    flex: 1;
}
.allocation-pool-unresolved-section {
    margin-top: 18px;
}
.allocation-pool-unresolved-rows {
    display: grid;
    gap: 6px;
}
.allocation-pool-unresolved-row {
    display: flex;
    flex-wrap: wrap;
    align-items: baseline;
    gap: 4px 12px;
    border: 1px solid #e5d1c4;
    border-radius: 6px;
    padding: 10px;
    font-size: 12px;
}
.allocation-pool-unresolved-row strong {
    min-width: 0;
    overflow-wrap: anywhere;
}
.allocation-pool-unresolved-row--highlighted,
.allocation-pool-unresolved-row:hover,
.allocation-pool-unresolved-row:focus-visible {
    border-color: #aa4315;
    background: #fcf3ee;
    outline-color: #aa4315;
    box-shadow: inset 3px 0 #aa4315;
}
.allocation-pool-empty {
    font-size: 12px;
    color: #64748b;
}
@media (max-width: 600px) {
    .allocation-pool-toggle {
        padding: 12px;
        gap: 8px;
    }
    .allocation-pool-summary {
        max-width: 45%;
    }
    .allocation-pool-worker {
        flex-wrap: wrap;
    }
}
</style>
